import { and, asc, eq, inArray } from "drizzle-orm";
import type { DbClient } from "@/server/platform/db/client";
import {
  productCategories,
  productCategoryLinks,
} from "@/server/platform/db/schema/products";
import { nowDate } from "@/shared/lib/date-time";
import type { DbTransaction } from "@/shared/types";

export type ProductCategoryRef = {
  id: string;
  name: string;
  sortOrder: number;
};

export async function syncProductCategoryLinks(
  client: DbTransaction | DbClient,
  productId: string,
  categoryIds: string[],
) {
  const uniqueIds = [...new Set(categoryIds.filter(Boolean))];
  if (uniqueIds.length === 0) {
    throw new Error("ຕ້ອງເລືອກຢ່າງນ້ອຍ 1 ຫມວດຫມູ່");
  }

  const existing = await client
    .select({ categoryId: productCategoryLinks.categoryId })
    .from(productCategoryLinks)
    .where(eq(productCategoryLinks.productId, productId));

  const existingIds = new Set(existing.map((row) => row.categoryId));
  const targetIds = new Set(uniqueIds);

  const toRemove = [...existingIds].filter((id) => !targetIds.has(id));
  const toAdd = uniqueIds.filter((id) => !existingIds.has(id));

  for (const categoryId of toRemove) {
    await client
      .delete(productCategoryLinks)
      .where(
        and(
          eq(productCategoryLinks.productId, productId),
          eq(productCategoryLinks.categoryId, categoryId),
        ),
      );
  }

  if (toAdd.length > 0) {
    const now = nowDate();
    await client.insert(productCategoryLinks).values(
      toAdd.map((categoryId) => ({
        productId,
        categoryId,
        createdAt: now,
      })),
    );
  }
}

export async function loadCategoriesByProductIds(
  client: DbTransaction | DbClient,
  productIds: string[],
): Promise<Map<string, ProductCategoryRef[]>> {
  const result = new Map<string, ProductCategoryRef[]>();
  if (productIds.length === 0) return result;

  const rows = await client
    .select({
      productId: productCategoryLinks.productId,
      id: productCategories.id,
      name: productCategories.name,
      sortOrder: productCategories.sortOrder,
    })
    .from(productCategoryLinks)
    .innerJoin(
      productCategories,
      eq(productCategoryLinks.categoryId, productCategories.id),
    )
    .where(inArray(productCategoryLinks.productId, productIds))
    .orderBy(
      asc(productCategoryLinks.productId),
      asc(productCategories.sortOrder),
      asc(productCategories.name),
    );

  for (const row of rows) {
    const list = result.get(row.productId) ?? [];
    list.push({
      id: row.id,
      name: row.name,
      sortOrder: row.sortOrder,
    });
    result.set(row.productId, list);
  }

  return result;
}

export function formatCategoryNames(categories: ProductCategoryRef[]): string {
  return categories.map((category) => category.name).join(" / ");
}
